import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { createMindmapStore } from './createStore';
import type { MindmapStoreApi } from './createStore';
import type { MindmapStore } from './types';

const StoreContext = createContext<MindmapStoreApi | null>(null);

const MISSING_PROVIDER =
  '[Custom-Mindmap] No store found. Render this inside <CustomMindmapProvider> or <CustomMindmap>.';

export type CustomMindmapProviderProps = { children?: ReactNode };

/** Owns one store for everything inside it, so hooks work outside the canvas too (D-018). */
export const CustomMindmapProvider = ({ children }: CustomMindmapProviderProps) => {
  const [store] = useState(createMindmapStore);
  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
};

/** Gives a ready-made store to the subtree (used by a canvas rendered without a provider, task 1.4). */
export const StoreProvider = StoreContext.Provider;

/** The nearest store, or `null` when there is no provider. */
export const useOptionalStoreApi = (): MindmapStoreApi | null => useContext(StoreContext);

/** The nearest store; throws when used outside a provider. */
export const useStoreApi = (): MindmapStoreApi => {
  const store = useOptionalStoreApi();
  if (!store) throw new Error(MISSING_PROVIDER);
  return store;
};

/** Subscribes to a slice of the store. Re-renders only when `equalityFn` says the slice changed. */
export const useMindmapStore = <TSlice,>(
  selector: (state: MindmapStore) => TSlice,
  equalityFn?: (a: TSlice, b: TSlice) => boolean,
): TSlice => useStoreWithEqualityFn(useStoreApi(), selector, equalityFn);
